import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Copy, Trash2, EyeOff, Eye, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { QuotationSelectionModal } from '@/components/quotation/QuotationSelectionModal';

interface PageActionsMenuProps {
  pageId: string;
  pageTitle: string;
  isVisible: boolean;
  isOpen: boolean;
  onClose: () => void;
  onToggleVisibility: (pageId: string) => void;
  onDuplicate: (pageId: string) => void;
  onDelete: (pageId: string) => void;
  canDelete: boolean;
  onCopyToQuotation?: (pageId: string, targetQuotationId: string) => void;
  currentQuotationId?: string;
  triggerRect?: DOMRect | null; // Nova prop para obter a posição do botão que acionou o menu
}

export function PageActionsMenu({
  pageId,
  pageTitle,
  isVisible,
  isOpen,
  onClose,
  onToggleVisibility,
  onDuplicate,
  onDelete,
  canDelete,
  onCopyToQuotation,
  currentQuotationId = '',
  triggerRect = null
}: PageActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [isQuotationSelectionOpen, setIsQuotationSelectionOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; left: number }>({ left: 0 });

  // Use effect to handle state changes and debugging
  useEffect(() => {
    // Removed console.log statement about isQuotationSelectionOpen
  }, [isQuotationSelectionOpen]);

  // Calcular a posição do menu com base no espaço disponível
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const menuHeight = menuRef.current.getBoundingClientRect().height;
      const windowHeight = window.innerHeight;
      
      // Posição padrão à direita do sidebar (considerando largura de 64px do sidebar)
      let left = 64; // Sidebar width
      
      // Se temos a posição do trigger, tentar posicionar próximo a ele
      if (triggerRect) {
        left = triggerRect.right;
        
        // Verificar se temos espaço suficiente abaixo do trigger
        const spaceBelow = windowHeight - triggerRect.bottom;
        
        if (spaceBelow >= menuHeight || spaceBelow > triggerRect.top) {
          // Mostrar menu abaixo do trigger se tiver espaço
          setMenuPosition({
            top: triggerRect.bottom + 4,
            left: Math.max(16, left - 120) // Ajustar para ficar centralizado horizontalmente
          });
        } else {
          // Mostrar menu acima do trigger se não tiver espaço abaixo
          setMenuPosition({
            bottom: windowHeight - triggerRect.top + 4,
            left: Math.max(16, left - 120)
          });
        }
      } else {
        // Fallback: posicionar no meio da tela ou na área visível
        setMenuPosition({
          top: Math.max(16, (windowHeight - menuHeight) / 2),
          left
        });
      }
    }
  }, [isOpen, triggerRect]);

  // Handle click outside to close menu - ignora cliques quando o modal de seleção está aberto
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isQuotationSelectionOpen) {
        // Se o modal de seleção estiver aberto, não fechar o menu
        return;
      }
      
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isQuotationSelectionOpen]);

  // Função direta para executar a exclusão
  const handleDirectDelete = () => {
    if (window.confirm("Tem certeza que deseja excluir esta página? Esta ação não pode ser desfeita.")) {
      try {
        onDelete(pageId);
        onClose();
      } catch (error) {
        console.error("Erro ao excluir página:", error);
      }
    }
  };

  // Função para abrir o modal de seleção de cotação
  const handleOpenQuotationSelection = useCallback(() => {
    // Removed console.log statement about handleOpenQuotationSelection
    setIsQuotationSelectionOpen(true);
  }, []);

  // Função para fechar o modal de seleção de cotação
  const handleCloseQuotationSelection = useCallback(() => {
    // Removed console.log statement about closing modal
    setIsQuotationSelectionOpen(false);
    onClose(); // Fechar o menu principal quando o modal for fechado
  }, [onClose]);

  // Função para lidar com a seleção da cotação de destino
  const handleSelectQuotation = useCallback((targetQuotationId: string) => {
    console.log('[PageActionsMenu] handleSelectQuotation chamado com:', { 
      targetQuotationId, 
      pageId, 
      hasCopyFunction: !!onCopyToQuotation 
    });
    
    if (onCopyToQuotation && pageId) {
      try {
        console.log('[PageActionsMenu] Chamando função onCopyToQuotation com parâmetros separados');
        // Passar parâmetros separadamente, não como objeto
        onCopyToQuotation(pageId, targetQuotationId);
        console.log('[PageActionsMenu] Função onCopyToQuotation executada com sucesso');
        
        // Open the target quotation in a new tab
        console.log('[PageActionsMenu] Abrindo nova aba com a cotação destino');
        const newWindow = window.open(`/admin/quotations/${targetQuotationId}`, '_blank');
        console.log('[PageActionsMenu] Nova aba aberta:', !!newWindow);
      } catch (error) {
        console.error('[PageActionsMenu] Erro ao copiar para cotação:', error);
      }
    }
    handleCloseQuotationSelection();
  }, [onCopyToQuotation, pageId, handleCloseQuotationSelection]);

  // Se o menu não estiver aberto e não houver seleção de cotação aberta, não renderizar nada
  if (!isOpen && !isQuotationSelectionOpen) return null;

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-[100]"
          onClick={(e) => {
            // Se o modal de seleção estiver aberto, não propagar o clique
            if (isQuotationSelectionOpen) {
              e.stopPropagation();
              return;
            }
          }}
        >
          <div 
            ref={menuRef}
            className="fixed bg-white rounded-lg overflow-hidden shadow-xl z-[101] w-48 max-w-full"
            style={{
              top: menuPosition.top,
              bottom: menuPosition.bottom,
              left: menuPosition.left,
            }}
          >
            <div className="p-2 space-y-1">
              {/* Toggle visibility */}
              <button
                onClick={() => {
                  onToggleVisibility(pageId);
                  onClose();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-voah-coral hover:text-voah-coral hover:bg-voah-coral/10"
              >
                {isVisible ? (
                  <>
                    <EyeOff className="h-4 w-4" />
                    <span>Visibilidade</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    <span>Visibilidade</span>
                  </>
                )}
              </button>
              
              {/* Duplicate */}
              <button
                onClick={() => {
                  onDuplicate(pageId);
                  onClose();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-voah-coral hover:text-voah-coral hover:bg-voah-coral/10"
              >
                <Copy className="h-4 w-4" />
                <span>Duplicar página</span>
              </button>
              
              {/* Copy to another quotation */}
              {onCopyToQuotation && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenQuotationSelection();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-voah-coral hover:text-voah-coral hover:bg-voah-coral/10"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Copiar para outra cotação</span>
                </button>
              )}
              
              {/* Delete with direct confirmation */}
              {canDelete && (
                <button
                  onClick={handleDirectDelete}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-red-500 hover:text-red-600 hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Excluir página</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para seleção de cotação destino - renderizado independentemente do menu principal */}
      {isQuotationSelectionOpen && (
        <QuotationSelectionModal
          isOpen={isQuotationSelectionOpen}
          onClose={handleCloseQuotationSelection}
          onSelect={handleSelectQuotation}
          currentQuotationId={currentQuotationId}
        />
      )}
    </>
  );
} 